import React from 'react';
import {DeclutterReport} from '../service/DeclutterApi';
import {actionLabel, outcomeLabel, reportTitle} from '../support/declutterLabels';

/**
 * Отчёт по разбору.
 *
 * Две стадии различаются намеренно. «Итоги решений» — сразу после сессии; освобождённое место
 * там не заявляется, потому что его ещё нет. «Итоги разбора» появляются после подтверждения
 * выбытия, и объём с весом подписаны как оценка: реальных габаритов вещей продукт не знает.
 */
export default function DeclutterReportCard({report}: {report: DeclutterReport}) {
    const isFinal = report.stage === 'FINAL';
    const freedVolume = Number(report.freedCubicMeters ?? 0);
    const freedWeight = Number(report.freedKilograms ?? 0);

    return (
        <div className="card p-4" data-testid="declutter-report">
            <h3 className="text-sm font-medium text-ink-900 dark:text-ink-100"
                data-testid="declutter-report-title">
                {reportTitle(report.stage)}
            </h3>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <section>
                    <h4 className="text-xs uppercase tracking-wide text-ink-500">Решения</h4>
                    <dl className="mt-1 text-sm text-ink-700 dark:text-ink-300">
                        {Object.entries(report.decisions).map(([action, count]) => (
                            <div key={action} className="flex justify-between gap-3">
                                <dt>{actionLabel(action)}</dt>
                                <dd className="tabular-nums">{count}</dd>
                            </div>
                        ))}
                        {Object.keys(report.decisions).length === 0 && (
                            <div><dt className="text-ink-500">Решений пока нет</dt></div>
                        )}
                    </dl>
                </section>
                <section>
                    <h4 className="text-xs uppercase tracking-wide text-ink-500">Что произошло</h4>
                    <dl className="mt-1 text-sm text-ink-700 dark:text-ink-300">
                        {Object.entries(report.outcomes).map(([outcome, count]) => (
                            <div key={outcome} className="flex justify-between gap-3">
                                <dt>{outcomeLabel(outcome)}</dt>
                                <dd className="tabular-nums">{count}</dd>
                            </div>
                        ))}
                        {Object.keys(report.outcomes).length === 0 && (
                            <div><dt className="text-ink-500">Вещи ещё не выбыли</dt></div>
                        )}
                    </dl>
                </section>
            </div>

            {report.pendingCount > 0 && (
                <p className="mt-3 text-sm text-amber-600 dark:text-amber-400"
                   data-testid="declutter-report-pending">
                    Ждут физического шага: {report.pendingCount}. Пока вещи в шкафу, место не освобождено.
                </p>
            )}

            {isFinal && (
                <div className="mt-3" data-testid="declutter-report-freed">
                    <p className="text-sm text-ink-800 dark:text-ink-200">
                        Освобождено ≈ {freedVolume.toFixed(2)} м³ · ≈ {freedWeight.toFixed(1)} кг
                    </p>
                    {report.estimated && (
                        <p className="text-xs text-ink-500 dark:text-ink-400">
                            Оценка по категориям: точных габаритов вещей приложение не знает.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
